import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { PasswordInput } from "@/components/ui/password-input";
import { toast } from "sonner";

const PASSWORD_REQUIREMENTS = [
  "Minimálně 6 znaků",
  "Alespoň jedno velké písmeno",
  "Alespoň jedno malé písmeno",
  "Alespoň jedno číslo"
];

export const RegisterForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { signUp, loading } = useAuth();
  const navigate = useNavigate();

  const validatePassword = () => {
    if (password !== confirmPassword) {
      setError("Hesla se neshodují");
      return false;
    }
    if (password.length < 6) {
      setError("Heslo musí mít alespoň 6 znaků");
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      setError("Heslo musí obsahovat alespoň jedno velké písmeno");
      return false;
    }
    if (!/[a-z]/.test(password)) {
      setError("Heslo musí obsahovat alespoň jedno malé písmeno");
      return false;
    }
    if (!/[0-9]/.test(password)) {
      setError("Heslo musí obsahovat alespoň jedno číslo");
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password || !confirmPassword) {
      setError("Prosím vyplňte všechna pole");
      return;
    }

    if (!validatePassword()) {
      return;
    }

    try {
      await signUp(email, password);
      toast.success("Registrace proběhla úspěšně! Prosím zkontrolujte svůj email pro potvrzení účtu.");
    } catch (err) {
      setError("Nepodařilo se vytvořit účet");
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl">Vytvořit účet</CardTitle>
        <CardDescription>
          Zaregistrujte se pro začátek práce s virtuálním zařizováním
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="vas@email.cz"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className={error ? "border-red-500" : ""}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Heslo</Label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              error={!!error}
            />
            <div className="text-sm text-gray-500">
              <p className="font-medium mb-1">Požadavky na heslo:</p>
              <ul className="list-disc list-inside space-y-1">
                {PASSWORD_REQUIREMENTS.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Potvrzení hesla</Label>
            <PasswordInput
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              error={!!error}
            />
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Vytváření účtu...
              </>
            ) : (
              "Vytvořit účet"
            )}
          </Button>
          <div className="text-center text-sm">
            Již máte účet?{" "}
            <Link to="/login" className="text-[#14A48D] hover:underline">
              Přihlásit se
            </Link>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
};
